import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { EmailStep } from './EmailStep';
import { OtpStep } from './OtpStep';
import { parseSendOtpError, parseResendOtpError, parseVerifyOtpError } from './loginErrors';
import { formatSessionDateTime } from '../../utils/formatSessionDateTime';

const STATES = { EMAIL: 0, OTP_VERIFY: 1 };
const RESEND_COOLDOWN_S = 60;

export default function LoginForm() {
  const navigate = useNavigate();
  const { sendOtp, verifyOtp, trustLogin } = useAuth();

  const [state, setState] = useState(STATES.EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCd, setResendCd] = useState(0);
  const [trustDevice, setTrustDevice] = useState(false);
  const emailRef = useRef(null);

  const { isAuthenticated } = useAuth();
  useEffect(() => { if (isAuthenticated) navigate('/', { replace: true }); }, [isAuthenticated, navigate]);
  useEffect(() => { if (state === STATES.EMAIL) emailRef.current?.focus(); }, [state]);
  useEffect(() => {
    if (resendCd <= 0) return;
    const timer = setTimeout(() => setResendCd(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCd]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validEmail) { setError('Enter a valid email address'); return; }
    setLoading(true); setError('');
    try {
      const res = await sendOtp(email);
      // Trusted-device short circuit: backend recognised this browser, skip OTP
      if (res?.trusted && trustLogin) {
        try {
          const loginData = await trustLogin(email);
          // One-shot toast — surfaces *why* the OTP step was skipped so
          // users don't think their auth is broken (they were just on a
          // browser that was previously marked "trust this device").
          const establishedAt = loginData?.trust_meta?.established_at;
          if (establishedAt) {
            toast.success('Signed in via trusted device', {
              description: `Last OTP entry · ${formatSessionDateTime(establishedAt)}`,
              duration: 6000,
            });
          }
          navigate('/', { replace: true });
          return;
        } catch (trustErr) {
          // Fall through to standard OTP flow if trust-login fails
          console.warn('[LoginForm] trust-login failed, falling back to OTP:', trustErr);
        }
      }
      if (res?.email_delivery && res.email_delivery !== 'sent') {
        setError("We couldn't send the sign-in email. Please try again in a moment or contact your administrator.");
        return;
      }
      setState(STATES.OTP_VERIFY);
      setResendCd(RESEND_COOLDOWN_S);
    } catch (err) {
      console.error('[LoginForm] Send OTP error:', err);
      setError(parseSendOtpError(err));
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 6) { setError('Enter the 6-digit code'); return; }
    setLoading(true); setError('');
    try {
      await verifyOtp(email, otp, { trustDevice });
      navigate('/', { replace: true });
    } catch (err) {
      setError(parseVerifyOtpError(err));
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (resendCd > 0) return;
    setLoading(true);
    try {
      const res = await sendOtp(email);
      if (res?.email_delivery && res.email_delivery !== 'sent') {
        setError("We couldn't send the sign-in email. Please try again in a moment or contact your administrator.");
        return;
      }
      setResendCd(RESEND_COOLDOWN_S);
      setError('');
    } catch (err) {
      setError(parseResendOtpError(err));
    } finally { setLoading(false); }
  };

  const goBack = () => { setError(''); setOtp(''); setState(STATES.EMAIL); };

  return (
    <div className="w-full" data-testid="login-page">
      {state === STATES.EMAIL && (
        <EmailStep
          ref={emailRef}
          email={email}
          onEmailChange={(e) => { setEmail(e.target.value); setError(''); }}
          onSubmit={handleEmailSubmit}
          loading={loading}
          error={error}
          validEmail={validEmail}
        />
      )}

      {state === STATES.OTP_VERIFY && (
        <OtpStep
          email={email}
          otp={otp}
          onOtpChange={(e) => { setOtp(e.target.value.replace(/[^0-9]/g, '')); setError(''); }}
          onSubmit={handleVerifyOtp}
          onResend={handleResend}
          onBack={goBack}
          loading={loading}
          error={error}
          resendCd={resendCd}
          trustDevice={trustDevice}
          onTrustDeviceChange={setTrustDevice}
        />
      )}
    </div>
  );
}
